import { StyleSheet, Text as RNText, View } from 'react-native';

import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import type { MeStats } from '@/types/earning';

import { Card } from './Card';
import { ErrorState } from './ErrorState';
import { MoneyDisplay } from './MoneyDisplay';
import { Overline } from './Overline';
import { Skeleton } from './Skeleton';

export interface ProfileStatsCardProps {
  stats: MeStats | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /**
   * Total signed-up referrals (from referralStatsStore.totals.conversions).
   * Profile is identity-shaped, not transactional — the referrer-count
   * pride metric leads, with lifetime + monthly dollars as supporting
   * context. Balance lives on the cashout flow instead.
   */
  referralCount: number | null;
}

export function ProfileStatsCard({
  stats,
  loading,
  error,
  onRetry,
  referralCount,
}: ProfileStatsCardProps) {
  if (loading && !stats) {
    return (
      <Card variant="dark" style={styles.card} testID="stats-skeleton">
        <View style={styles.row}>
          <View style={styles.item}>
            <Skeleton width={80} height={12} radius="sm" />
            <Skeleton width={60} height={28} radius="sm" />
          </View>
          <View style={styles.item}>
            <Skeleton width={80} height={12} radius="sm" />
            <Skeleton width={60} height={28} radius="sm" />
          </View>
          <View style={styles.item}>
            <Skeleton width={80} height={12} radius="sm" />
            <Skeleton width={60} height={28} radius="sm" />
          </View>
        </View>
      </Card>
    );
  }

  if (error && !stats) {
    return (
      <ErrorState
        title="Stats unavailable"
        body="Couldn't load your stats. Try again."
        onRetry={onRetry}
        testID="stats-error"
      />
    );
  }

  if (!stats) return null;

  return (
    <Card variant="dark" style={styles.card} testID="stats-card">
      <View style={styles.row}>
        <View style={styles.item}>
          <Overline tone="onDark">Referrals</Overline>
          <RNText style={styles.countDisplay}>{referralCount ?? '—'}</RNText>
        </View>
        <View style={styles.item}>
          <Overline tone="onDark">Lifetime</Overline>
          <MoneyDisplay cents={stats.lifetime_earned_cents} size="sm" tone="onDark" />
        </View>
        <View style={styles.item}>
          <Overline tone="onDark">This Month</Overline>
          <MoneyDisplay cents={stats.month_earnings_cents} size="sm" tone="onDark" />
        </View>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    marginBottom: spacing.lg,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  item: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  // Mirror MoneyDisplay size="sm" amount style so the Referrals number
  // sits at the same visual height as the dollar-amount tiles.
  countDisplay: {
    fontFamily: fonts.numeric,
    fontVariant: ['tabular-nums'],
    fontSize: fontSizes.lg,
    letterSpacing: -1,
    color: colors.frost,
  },
});
